import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  SimpleTable,
  useToast,
} from "@swordsweeper/ssi-ui-component-library";
import DistributorLogo from "@src/components/common/DistributorLogo";
import Menu, { MenuItem, MenuThemes } from "@src/components/common/Menu";
import SpinningLoader from "@src/components/common/SpinningLoader/SpinningLoader";
import { formatCurrency } from "@src/utils/currency";
import { useCurrentUserStore } from "@src/state/currentUserState";
import { DistributorConnection } from "@src/state/currentUserState";
import {
  DistributorConnectionLabels,
  DistributorConnectionStatus,
  DistributorConnectionStatuses,
} from "@src/components/widgets/AccountDistributorSettings/DistributorConnectionConstants";
import * as AuthApi from "@src/api/AuthApi";
import { setPreferredDistributor } from "@src/api/DistributorApi";
import styles from "./ClinicSuppliersTable.module.scss";

function statusLabel(status: DistributorConnectionStatus): string {
  return DistributorConnectionLabels[status] ?? "Unknown";
}

export default function ClinicSuppliersTable(): React.JSX.Element {
  const navigate = useNavigate();
  const showToast = useToast();
  const {
    distributorConnections,
    loadingDistributorConnections,
    fetchDistributorConnections,
  } = useCurrentUserStore();
  const [busyDistributor, setBusyDistributor] = useState<string | null>(null);

  useEffect(() => {
    if (!loadingDistributorConnections && !distributorConnections) {
      fetchDistributorConnections({ includeOAuthLink: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () =>
    fetchDistributorConnections({ includeOAuthLink: true });

  const handleManage = () => navigate("/account/distributors");

  const handleTogglePreferred = async (c: DistributorConnection) => {
    setBusyDistributor(c.distributor);
    try {
      await setPreferredDistributor(c.distributor, !c.isPreferred);
      await refresh();
      showToast({
        message: c.isPreferred
          ? "Removed preferred supplier."
          : "Marked as preferred supplier.",
        variant: "success",
      });
    } catch (err: any) {
      showToast({
        message:
          err?.response?.body?.error ||
          err?.message ||
          "Could not update preferred status.",
        variant: "error",
      });
    } finally {
      setBusyDistributor(null);
    }
  };

  const handleDisconnect = async (c: DistributorConnection) => {
    if (!c.credentialId) {
      // No credentials means we can't disconnect inline — fall back to the
      // full management surface where OAuth disconnects (eg. Covetrus) live.
      navigate("/account/distributors");
      return;
    }
    const confirmed = window.confirm(
      `Disconnect this supplier from the clinic? You can reconnect later.`,
    );
    if (!confirmed) return;

    setBusyDistributor(c.distributor);
    try {
      await AuthApi.deleteDistributorAuthCredentials(c.credentialId);
      await refresh();
      showToast({ message: "Supplier disconnected.", variant: "success" });
    } catch (err: any) {
      showToast({
        message:
          err?.response?.body?.error ||
          err?.message ||
          "Could not disconnect supplier.",
        variant: "error",
      });
    } finally {
      setBusyDistributor(null);
    }
  };

  if (loadingDistributorConnections && !distributorConnections) {
    return <SpinningLoader />;
  }

  const rows = distributorConnections ?? [];

  return (
    <SimpleTable
      className={styles.table}
      columns={[
        {
          header: "Supplier",
          key: "distributor",
          render: (c: DistributorConnection) => (
            <span className={styles.supplierCell}>
              <DistributorLogo
                distributorSlug={c.distributor}
                height={24}
              />
              {c.isPreferred && (
                <i
                  className={`fa-solid fa-star-sharp ${styles.preferredStar}`}
                  aria-label="Preferred supplier"
                />
              )}
            </span>
          ),
        },
        {
          header: "Status",
          key: "status",
          render: (c: DistributorConnection) => (
            <span className={styles.cell}>{statusLabel(c.status)}</span>
          ),
        },
        {
          header: "Spent Last Month",
          key: "amountSpentLastMonth",
          render: (c: DistributorConnection) => (
            <span className={styles.cell}>
              {formatCurrency(c.amountSpentLastMonth ?? 0)}
            </span>
          ),
        },
        {
          header: "Spent YTD",
          key: "amountSpentYearToDate",
          render: (c: DistributorConnection) => (
            <span className={styles.cell}>
              {formatCurrency(c.amountSpentYearToDate ?? 0)}
            </span>
          ),
        },
        {
          header: "",
          key: "actions",
          render: (c: DistributorConnection) => {
            const isConnected =
              c.status === DistributorConnectionStatuses.Connected;
            const isBusy = busyDistributor === c.distributor;
            return (
              <span className="padding-left-sm">
                <Menu
                  buttonContent={
                    <div className="padding-x-sm">
                      <i
                        className={`fa-light fa-ellipsis-v ${styles.kebab}`}
                      />
                    </div>
                  }
                  theme={MenuThemes.Small}
                >
                  {isConnected ? (
                    <>
                      <MenuItem>
                        <Button
                          className="width-100"
                          label={
                            c.isPreferred
                              ? "Remove Preferred"
                              : "Mark Preferred"
                          }
                          onClick={() => handleTogglePreferred(c)}
                          disabled={isBusy}
                          data-testid={`clinic-suppliers-table-toggle-preferred-${c.distributor}`}
                        />
                      </MenuItem>
                      <MenuItem>
                        <Button
                          className="width-100"
                          label="Disconnect"
                          color="#F80A33"
                          hoverColor="#FF98AA"
                          onClick={() => handleDisconnect(c)}
                          disabled={isBusy}
                          data-testid={`clinic-suppliers-table-disconnect-${c.distributor}`}
                        />
                      </MenuItem>
                    </>
                  ) : (
                    <MenuItem>
                      <Button
                        className="width-100"
                        label="Connect"
                        onClick={handleManage}
                        data-testid={`clinic-suppliers-table-connect-${c.distributor}`}
                      />
                    </MenuItem>
                  )}
                  <MenuItem>
                    <Button
                      className="width-100"
                      label="Manage Connection"
                      onClick={handleManage}
                    />
                  </MenuItem>
                </Menu>
              </span>
            );
          },
        },
      ]}
      data={rows}
    />
  );
}
